/**
 * ISO-3166-1 alpha-2 country list — used by the clinic onboarding flow
 * (`/onboard`) and the Physio Network filter to let clinics pick any
 * country in the world, not just the 10 hard-coded ones we shipped in
 * Iter 1.
 *
 * The `region` field is used by the onboarding UI to group countries in
 * `<optgroup>`s so a select with 249 rows is still browsable. Regions map
 * to UN geoscheme + a few conveniences (e.g. "EU-27" kept as its own
 * bucket because Poland-first clinics reach for it constantly).
 *
 * `popular` = shown at the top of the list. Anything with a healthy
 * physio market or a known cluster of Physiogenic clinics gets flagged.
 *
 * Sorted alphabetically by name inside each region. NEVER reuse an ISO
 * code; if you add a new territory just append it — Mongo persists the
 * `alpha2` value on `clinics.country_code`.
 */
export const COUNTRIES = [
  // ─── Europe ───────────────────────────────────────────────────────
  { code: 'AL', name: 'Albania',              region: 'Europe' },
  { code: 'AD', name: 'Andorra',              region: 'Europe' },
  { code: 'AT', name: 'Austria',              region: 'EU-27',   popular: true },
  { code: 'BY', name: 'Belarus',              region: 'Europe' },
  { code: 'BE', name: 'Belgium',              region: 'EU-27',   popular: true },
  { code: 'BA', name: 'Bosnia and Herzegovina', region: 'Europe' },
  { code: 'BG', name: 'Bulgaria',             region: 'EU-27' },
  { code: 'HR', name: 'Croatia',              region: 'EU-27' },
  { code: 'CY', name: 'Cyprus',               region: 'EU-27' },
  { code: 'CZ', name: 'Czech Republic',       region: 'EU-27',   popular: true },
  { code: 'DK', name: 'Denmark',              region: 'EU-27',   popular: true },
  { code: 'EE', name: 'Estonia',              region: 'EU-27' },
  { code: 'FO', name: 'Faroe Islands',        region: 'Europe' },
  { code: 'FI', name: 'Finland',              region: 'EU-27' },
  { code: 'FR', name: 'France',               region: 'EU-27',   popular: true },
  { code: 'DE', name: 'Germany',              region: 'EU-27',   popular: true },
  { code: 'GI', name: 'Gibraltar',            region: 'Europe' },
  { code: 'GR', name: 'Greece',               region: 'EU-27' },
  { code: 'GG', name: 'Guernsey',             region: 'Europe' },
  { code: 'HU', name: 'Hungary',              region: 'EU-27' },
  { code: 'IS', name: 'Iceland',              region: 'Europe' },
  { code: 'IE', name: 'Ireland',              region: 'EU-27',   popular: true },
  { code: 'IM', name: 'Isle of Man',          region: 'Europe' },
  { code: 'IT', name: 'Italy',                region: 'EU-27',   popular: true },
  { code: 'JE', name: 'Jersey',               region: 'Europe' },
  { code: 'XK', name: 'Kosovo',               region: 'Europe' },
  { code: 'LV', name: 'Latvia',               region: 'EU-27' },
  { code: 'LI', name: 'Liechtenstein',        region: 'Europe' },
  { code: 'LT', name: 'Lithuania',            region: 'EU-27' },
  { code: 'LU', name: 'Luxembourg',           region: 'EU-27' },
  { code: 'MT', name: 'Malta',                region: 'EU-27' },
  { code: 'MD', name: 'Moldova',              region: 'Europe' },
  { code: 'MC', name: 'Monaco',               region: 'Europe' },
  { code: 'ME', name: 'Montenegro',           region: 'Europe' },
  { code: 'NL', name: 'Netherlands',          region: 'EU-27',   popular: true },
  { code: 'MK', name: 'North Macedonia',      region: 'Europe' },
  { code: 'NO', name: 'Norway',               region: 'Europe',  popular: true },
  { code: 'PL', name: 'Poland',               region: 'EU-27',   popular: true },
  { code: 'PT', name: 'Portugal',             region: 'EU-27',   popular: true },
  { code: 'RO', name: 'Romania',              region: 'EU-27' },
  { code: 'RU', name: 'Russia',               region: 'Europe' },
  { code: 'SM', name: 'San Marino',           region: 'Europe' },
  { code: 'RS', name: 'Serbia',               region: 'Europe' },
  { code: 'SK', name: 'Slovakia',             region: 'EU-27' },
  { code: 'SI', name: 'Slovenia',             region: 'EU-27' },
  { code: 'ES', name: 'Spain',                region: 'EU-27',   popular: true },
  { code: 'SJ', name: 'Svalbard and Jan Mayen', region: 'Europe' },
  { code: 'SE', name: 'Sweden',               region: 'EU-27',   popular: true },
  { code: 'CH', name: 'Switzerland',          region: 'Europe',  popular: true },
  { code: 'UA', name: 'Ukraine',              region: 'Europe' },
  { code: 'GB', name: 'United Kingdom',       region: 'Europe',  popular: true },
  { code: 'VA', name: 'Vatican City',         region: 'Europe' },

  // ─── Americas ────────────────────────────────────────────────────
  { code: 'AI', name: 'Anguilla',             region: 'Americas' },
  { code: 'AG', name: 'Antigua and Barbuda',  region: 'Americas' },
  { code: 'AR', name: 'Argentina',            region: 'Americas' },
  { code: 'AW', name: 'Aruba',                region: 'Americas' },
  { code: 'BS', name: 'Bahamas',              region: 'Americas' },
  { code: 'BB', name: 'Barbados',             region: 'Americas' },
  { code: 'BZ', name: 'Belize',               region: 'Americas' },
  { code: 'BM', name: 'Bermuda',              region: 'Americas' },
  { code: 'BO', name: 'Bolivia',              region: 'Americas' },
  { code: 'BQ', name: 'Bonaire, Sint Eustatius & Saba', region: 'Americas' },
  { code: 'BR', name: 'Brazil',               region: 'Americas', popular: true },
  { code: 'CA', name: 'Canada',               region: 'Americas', popular: true },
  { code: 'KY', name: 'Cayman Islands',       region: 'Americas' },
  { code: 'CL', name: 'Chile',                region: 'Americas', popular: true },
  { code: 'CO', name: 'Colombia',             region: 'Americas' },
  { code: 'CR', name: 'Costa Rica',           region: 'Americas' },
  { code: 'CU', name: 'Cuba',                 region: 'Americas' },
  { code: 'CW', name: 'Curaçao',              region: 'Americas' },
  { code: 'DM', name: 'Dominica',             region: 'Americas' },
  { code: 'DO', name: 'Dominican Republic',   region: 'Americas' },
  { code: 'EC', name: 'Ecuador',              region: 'Americas' },
  { code: 'SV', name: 'El Salvador',          region: 'Americas' },
  { code: 'FK', name: 'Falkland Islands',     region: 'Americas' },
  { code: 'GF', name: 'French Guiana',        region: 'Americas' },
  { code: 'GL', name: 'Greenland',            region: 'Americas' },
  { code: 'GD', name: 'Grenada',              region: 'Americas' },
  { code: 'GP', name: 'Guadeloupe',           region: 'Americas' },
  { code: 'GT', name: 'Guatemala',            region: 'Americas' },
  { code: 'GY', name: 'Guyana',               region: 'Americas' },
  { code: 'HT', name: 'Haiti',                region: 'Americas' },
  { code: 'HN', name: 'Honduras',             region: 'Americas' },
  { code: 'JM', name: 'Jamaica',              region: 'Americas' },
  { code: 'MQ', name: 'Martinique',           region: 'Americas' },
  { code: 'MX', name: 'Mexico',               region: 'Americas', popular: true },
  { code: 'MS', name: 'Montserrat',           region: 'Americas' },
  { code: 'NI', name: 'Nicaragua',            region: 'Americas' },
  { code: 'PA', name: 'Panama',               region: 'Americas' },
  { code: 'PY', name: 'Paraguay',             region: 'Americas' },
  { code: 'PE', name: 'Peru',                 region: 'Americas' },
  { code: 'PR', name: 'Puerto Rico',          region: 'Americas' },
  { code: 'BL', name: 'Saint Barthélemy',     region: 'Americas' },
  { code: 'KN', name: 'Saint Kitts and Nevis',region: 'Americas' },
  { code: 'LC', name: 'Saint Lucia',          region: 'Americas' },
  { code: 'MF', name: 'Saint Martin (French)',region: 'Americas' },
  { code: 'PM', name: 'Saint Pierre & Miquelon', region: 'Americas' },
  { code: 'VC', name: 'Saint Vincent & the Grenadines', region: 'Americas' },
  { code: 'SX', name: 'Sint Maarten',         region: 'Americas' },
  { code: 'SR', name: 'Suriname',             region: 'Americas' },
  { code: 'TT', name: 'Trinidad and Tobago',  region: 'Americas' },
  { code: 'TC', name: 'Turks and Caicos',     region: 'Americas' },
  { code: 'US', name: 'United States',        region: 'Americas', popular: true },
  { code: 'UY', name: 'Uruguay',              region: 'Americas' },
  { code: 'VE', name: 'Venezuela',            region: 'Americas' },
  { code: 'VG', name: 'Virgin Islands (British)', region: 'Americas' },
  { code: 'VI', name: 'Virgin Islands (US)',  region: 'Americas' },

  // ─── Africa ──────────────────────────────────────────────────────
  { code: 'DZ', name: 'Algeria',              region: 'Africa' },
  { code: 'AO', name: 'Angola',               region: 'Africa' },
  { code: 'BJ', name: 'Benin',                region: 'Africa' },
  { code: 'BW', name: 'Botswana',             region: 'Africa' },
  { code: 'BF', name: 'Burkina Faso',         region: 'Africa' },
  { code: 'BI', name: 'Burundi',              region: 'Africa' },
  { code: 'CV', name: 'Cabo Verde',           region: 'Africa' },
  { code: 'CM', name: 'Cameroon',             region: 'Africa' },
  { code: 'CF', name: 'Central African Rep.', region: 'Africa' },
  { code: 'TD', name: 'Chad',                 region: 'Africa' },
  { code: 'KM', name: 'Comoros',              region: 'Africa' },
  { code: 'CG', name: 'Congo (Brazzaville)',  region: 'Africa' },
  { code: 'CD', name: 'Congo (Kinshasa)',     region: 'Africa' },
  { code: 'CI', name: "Côte d'Ivoire",        region: 'Africa' },
  { code: 'DJ', name: 'Djibouti',             region: 'Africa' },
  { code: 'EG', name: 'Egypt',                region: 'Africa' },
  { code: 'GQ', name: 'Equatorial Guinea',    region: 'Africa' },
  { code: 'ER', name: 'Eritrea',              region: 'Africa' },
  { code: 'SZ', name: 'Eswatini',             region: 'Africa' },
  { code: 'ET', name: 'Ethiopia',             region: 'Africa' },
  { code: 'GA', name: 'Gabon',                region: 'Africa' },
  { code: 'GM', name: 'Gambia',               region: 'Africa' },
  { code: 'GH', name: 'Ghana',                region: 'Africa' },
  { code: 'GN', name: 'Guinea',               region: 'Africa' },
  { code: 'GW', name: 'Guinea-Bissau',        region: 'Africa' },
  { code: 'KE', name: 'Kenya',                region: 'Africa' },
  { code: 'LS', name: 'Lesotho',              region: 'Africa' },
  { code: 'LR', name: 'Liberia',              region: 'Africa' },
  { code: 'LY', name: 'Libya',                region: 'Africa' },
  { code: 'MG', name: 'Madagascar',           region: 'Africa' },
  { code: 'MW', name: 'Malawi',               region: 'Africa' },
  { code: 'ML', name: 'Mali',                 region: 'Africa' },
  { code: 'MR', name: 'Mauritania',           region: 'Africa' },
  { code: 'MU', name: 'Mauritius',            region: 'Africa' },
  { code: 'YT', name: 'Mayotte',              region: 'Africa' },
  { code: 'MA', name: 'Morocco',              region: 'Africa' },
  { code: 'MZ', name: 'Mozambique',           region: 'Africa' },
  { code: 'NA', name: 'Namibia',              region: 'Africa' },
  { code: 'NE', name: 'Niger',                region: 'Africa' },
  { code: 'NG', name: 'Nigeria',              region: 'Africa' },
  { code: 'RE', name: 'Réunion',              region: 'Africa' },
  { code: 'RW', name: 'Rwanda',               region: 'Africa' },
  { code: 'SH', name: 'Saint Helena',         region: 'Africa' },
  { code: 'ST', name: 'São Tomé & Príncipe',  region: 'Africa' },
  { code: 'SN', name: 'Senegal',              region: 'Africa' },
  { code: 'SC', name: 'Seychelles',           region: 'Africa' },
  { code: 'SL', name: 'Sierra Leone',         region: 'Africa' },
  { code: 'SO', name: 'Somalia',              region: 'Africa' },
  { code: 'ZA', name: 'South Africa',         region: 'Africa',  popular: true },
  { code: 'SS', name: 'South Sudan',          region: 'Africa' },
  { code: 'SD', name: 'Sudan',                region: 'Africa' },
  { code: 'TZ', name: 'Tanzania',             region: 'Africa' },
  { code: 'TG', name: 'Togo',                 region: 'Africa' },
  { code: 'TN', name: 'Tunisia',              region: 'Africa' },
  { code: 'UG', name: 'Uganda',               region: 'Africa' },
  { code: 'EH', name: 'Western Sahara',       region: 'Africa' },
  { code: 'ZM', name: 'Zambia',               region: 'Africa' },
  { code: 'ZW', name: 'Zimbabwe',             region: 'Africa' },

  // ─── Asia ────────────────────────────────────────────────────────
  { code: 'AF', name: 'Afghanistan',          region: 'Asia' },
  { code: 'AM', name: 'Armenia',              region: 'Asia' },
  { code: 'AZ', name: 'Azerbaijan',           region: 'Asia' },
  { code: 'BH', name: 'Bahrain',              region: 'Asia' },
  { code: 'BD', name: 'Bangladesh',           region: 'Asia' },
  { code: 'BT', name: 'Bhutan',               region: 'Asia' },
  { code: 'BN', name: 'Brunei',               region: 'Asia' },
  { code: 'KH', name: 'Cambodia',             region: 'Asia' },
  { code: 'CN', name: 'China',                region: 'Asia',    popular: true },
  { code: 'GE', name: 'Georgia',              region: 'Asia' },
  { code: 'HK', name: 'Hong Kong',            region: 'Asia' },
  { code: 'IN', name: 'India',                region: 'Asia',    popular: true },
  { code: 'ID', name: 'Indonesia',            region: 'Asia' },
  { code: 'IR', name: 'Iran',                 region: 'Asia' },
  { code: 'IQ', name: 'Iraq',                 region: 'Asia' },
  { code: 'IL', name: 'Israel',               region: 'Asia',    popular: true },
  { code: 'JP', name: 'Japan',                region: 'Asia',    popular: true },
  { code: 'JO', name: 'Jordan',               region: 'Asia' },
  { code: 'KZ', name: 'Kazakhstan',           region: 'Asia' },
  { code: 'KW', name: 'Kuwait',               region: 'Asia' },
  { code: 'KG', name: 'Kyrgyzstan',           region: 'Asia' },
  { code: 'LA', name: 'Laos',                 region: 'Asia' },
  { code: 'LB', name: 'Lebanon',              region: 'Asia' },
  { code: 'MO', name: 'Macau',                region: 'Asia' },
  { code: 'MY', name: 'Malaysia',             region: 'Asia' },
  { code: 'MV', name: 'Maldives',             region: 'Asia' },
  { code: 'MN', name: 'Mongolia',             region: 'Asia' },
  { code: 'MM', name: 'Myanmar',              region: 'Asia' },
  { code: 'NP', name: 'Nepal',                region: 'Asia' },
  { code: 'KP', name: 'North Korea',          region: 'Asia' },
  { code: 'OM', name: 'Oman',                 region: 'Asia' },
  { code: 'PK', name: 'Pakistan',             region: 'Asia' },
  { code: 'PS', name: 'Palestine',            region: 'Asia' },
  { code: 'PH', name: 'Philippines',          region: 'Asia' },
  { code: 'QA', name: 'Qatar',                region: 'Asia' },
  { code: 'SA', name: 'Saudi Arabia',         region: 'Asia' },
  { code: 'SG', name: 'Singapore',            region: 'Asia',    popular: true },
  { code: 'KR', name: 'South Korea',          region: 'Asia',    popular: true },
  { code: 'LK', name: 'Sri Lanka',            region: 'Asia' },
  { code: 'SY', name: 'Syria',                region: 'Asia' },
  { code: 'TW', name: 'Taiwan',               region: 'Asia' },
  { code: 'TJ', name: 'Tajikistan',           region: 'Asia' },
  { code: 'TH', name: 'Thailand',             region: 'Asia' },
  { code: 'TL', name: 'Timor-Leste',          region: 'Asia' },
  { code: 'TR', name: 'Türkiye',              region: 'Asia',    popular: true },
  { code: 'TM', name: 'Turkmenistan',         region: 'Asia' },
  { code: 'AE', name: 'United Arab Emirates', region: 'Asia',    popular: true },
  { code: 'UZ', name: 'Uzbekistan',           region: 'Asia' },
  { code: 'VN', name: 'Vietnam',              region: 'Asia' },
  { code: 'YE', name: 'Yemen',                region: 'Asia' },

  // ─── Oceania ─────────────────────────────────────────────────────
  { code: 'AS', name: 'American Samoa',       region: 'Oceania' },
  { code: 'AU', name: 'Australia',            region: 'Oceania', popular: true },
  { code: 'CK', name: 'Cook Islands',         region: 'Oceania' },
  { code: 'FJ', name: 'Fiji',                 region: 'Oceania' },
  { code: 'PF', name: 'French Polynesia',     region: 'Oceania' },
  { code: 'GU', name: 'Guam',                 region: 'Oceania' },
  { code: 'KI', name: 'Kiribati',             region: 'Oceania' },
  { code: 'MH', name: 'Marshall Islands',     region: 'Oceania' },
  { code: 'FM', name: 'Micronesia',           region: 'Oceania' },
  { code: 'NR', name: 'Nauru',                region: 'Oceania' },
  { code: 'NC', name: 'New Caledonia',        region: 'Oceania' },
  { code: 'NZ', name: 'New Zealand',          region: 'Oceania', popular: true },
  { code: 'NU', name: 'Niue',                 region: 'Oceania' },
  { code: 'NF', name: 'Norfolk Island',       region: 'Oceania' },
  { code: 'MP', name: 'Northern Mariana Is.', region: 'Oceania' },
  { code: 'PW', name: 'Palau',                region: 'Oceania' },
  { code: 'PG', name: 'Papua New Guinea',     region: 'Oceania' },
  { code: 'PN', name: 'Pitcairn Islands',     region: 'Oceania' },
  { code: 'WS', name: 'Samoa',                region: 'Oceania' },
  { code: 'SB', name: 'Solomon Islands',      region: 'Oceania' },
  { code: 'TK', name: 'Tokelau',              region: 'Oceania' },
  { code: 'TO', name: 'Tonga',                region: 'Oceania' },
  { code: 'TV', name: 'Tuvalu',               region: 'Oceania' },
  { code: 'VU', name: 'Vanuatu',              region: 'Oceania' },
  { code: 'WF', name: 'Wallis and Futuna',    region: 'Oceania' },
];

/** Region → list of countries in that region, sorted by name. */
export const COUNTRIES_BY_REGION = COUNTRIES.reduce((acc, c) => {
  (acc[c.region] = acc[c.region] || []).push(c);
  return acc;
}, {});
Object.values(COUNTRIES_BY_REGION).forEach(list =>
  list.sort((a, b) => a.name.localeCompare(b.name))
);

/** Regions in a stable display order — EU-27 first because Physiogenic is
 *  Poland-first, then the rest of Europe, then the world. */
export const REGION_ORDER = ['EU-27', 'Europe', 'Americas', 'Asia', 'Africa', 'Oceania'];

/** Popular countries, in a fixed order that keeps Poland first (product
 *  request) and then follows physio-market size. */
const POPULAR_ORDER = [
  'PL','DE','FR','ES','IT','NL','BE','AT','SE','DK','NO','CH','IE','PT','CZ',
  'GB','US','CA','MX','BR','CL',
  'AU','NZ','JP','KR','SG','IN','AE','IL','CN','TR',
  'ZA',
];
export const POPULAR_COUNTRIES = POPULAR_ORDER
  .map(code => COUNTRIES.find(c => c.code === code))
  .filter(Boolean);

/** Fast lookup: alpha-2 → country object. */
const _byCode = Object.fromEntries(COUNTRIES.map(c => [c.code, c]));
export function getCountry(code) {
  return _byCode[(code || '').toUpperCase()] || null;
}
export function getCountryName(code, fallback = '') {
  return _byCode[(code || '').toUpperCase()]?.name || fallback;
}

/**
 * Turn an ISO-3166 alpha-2 code into its Unicode flag emoji by mapping
 * each ASCII letter to the corresponding Regional Indicator Symbol
 * (code-point 0x1F1E6 for 'A'). Returns '' for invalid input rather than
 * throwing, so it's safe to drop into JSX.
 */
export function flagEmoji(code) {
  if (!code || typeof code !== 'string' || code.length !== 2) return '';
  const cc = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(
    0x1F1E6 + cc.charCodeAt(0) - 65,
    0x1F1E6 + cc.charCodeAt(1) - 65,
  );
}
